import { Calendar, Link as LinkIcon, Mail, X } from "lucide-react";
import { useState } from "react";
import { EditableReportScreen } from "./EditableReportScreen";
import { getFirebaseDB } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

interface ActionableSummaryProps {
  isOpen: boolean;
  onClose: () => void;
  observation?: string;
  recommendation?: string;
  topic?: string;
  eventType?: string;
  sentiment?: string;
  location?: string;
  date?: string;
}

export function ActionableSummary({
  isOpen,
  onClose,
  observation = "I saw several participants struggling to navigate the information system during the event. Most were unable to locate and share critical information in a timely manner and mistakes were made. This can compromise data quality, integrity and availability of information.",
  recommendation = "Enhance training for information systems and intelligence tools by setting measurable standards and coordinating with local authorities for annual updates based on modern capabilities and user feedback. Improve transparency and efficiency in decision-making.\n\nEstablish a support network of modern intelligence analysts, data specialists, trainers, and security experts to share best practices and ensure annual training stays relevant and effective.",
  topic = "AI Bias or Risk",
  eventType = "Event",
  sentiment = "Hopeless",
  location = "Jacksonville, NC (Camp Lejeune)",
  date = "Tuesday, January 6, 2026"
}: ActionableSummaryProps) {
  const [showEditableReport, setShowEditableReport] = useState(false);
  
  if (!isOpen) return null;

  const handleExport = async () => {
    // Save to Firebase first
    const db = getFirebaseDB();
    
    if (db) {
      try {
        const actionableSummariesCollection = collection(db, 'actionableSummaries');
        
        const summaryData = {
          observation,
          recommendation,
          topic,
          eventType,
          sentiment,
          location,
          date,
          createdAt: serverTimestamp()
        };

        const docRef = await addDoc(actionableSummariesCollection, summaryData);
        console.log('✅ Actionable Summary saved to Firebase with ID:', docRef.id);
      } catch (error) {
        console.error('❌ Error saving summary to Firebase:', error);
        // Continue to report screen even if Firebase save fails
      }
    }
    
    // Open the editable report screen with REPORT HEADER
    setShowEditableReport(true);
  };

  const handleEmail = () => {
    // Get user email if available
    const userEmail = localStorage.getItem('subscriberEmail') || '';
    
    // Create email subject
    const subject = `Actionable Summary - ${topic} - ${date}`;
    
    // Create email body with proper formatting
    const emailBody = `ACTIONABLE SUMMARY
${date}

OBSERVATION:
${observation}

RECOMMENDATION:
${recommendation}

METADATA:
• Topic: ${topic}
• Event Type: ${eventType}
• Sentiment: ${sentiment}
• Location: ${location}

---
This summary was generated by ORA AI Leadership Agent
Date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`;

    // Create mailto link with user email pre-populated
    const mailtoLink = `mailto:${userEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open default email client (Gmail, Outlook, etc.)
    window.location.href = mailtoLink;
  };

  return (
    <>
      <div className="absolute inset-0 bg-white z-[70] overflow-y-auto">
        {/* Content Container */}
        <div className="w-full h-full px-3 py-3 pt-[24px]">
          {/* Header */}
          <div className="mb-2 relative">
            <button
              onClick={onClose}
              className="absolute top-0 right-0 p-1.5 hover:bg-gray-100 rounded-lg transition-colors z-10"
              aria-label="Close"
            >
              <X className="size-4 text-gray-600" />
            </button>
            <h1 className="text-base font-bold text-center mb-1.5 text-black">Actionable Summary</h1>
            
            {/* Date */}
            <div className="flex items-center gap-1.5 text-[11px] text-black mb-2">
              <Calendar className="size-3.5" />
              <span className="text-gray-700">{date}</span>
            </div>
          </div>

          {/* Observation Section */}
          <div className="mb-3">
            <h2 className="text-sm font-bold mb-1.5 text-black">Observation:</h2>
            <p className="text-xs text-black leading-snug">
              {observation}
            </p>
          </div>

          {/* Recommendation Section */}
          <div className="mb-4">
            <h2 className="text-sm font-bold mb-1.5 text-black">Recommendation:</h2>
            <div className="text-xs text-black leading-snug space-y-2">
              {recommendation.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-black">{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="space-y-1.5 mb-6">
            <div className="flex items-start gap-1.5">
              <span className="text-xs text-black">•</span>
              <div className="flex gap-1.5 flex-wrap">
                <span className="font-bold text-xs text-black">Topic:</span>
                <span className="text-xs text-black">{topic}</span>
              </div>
            </div>
            
            <div className="flex items-start gap-1.5">
              <span className="text-xs text-black">•</span>
              <div className="flex gap-1.5 flex-wrap">
                <span className="font-bold text-xs text-black">Event Type:</span>
                <span className="text-xs text-black">{eventType}</span>
              </div>
            </div>
            
            <div className="flex items-start gap-1.5">
              <span className="text-xs text-black">•</span>
              <div className="flex gap-1.5 flex-wrap">
                <span className="font-bold text-xs text-black">Sentiment:</span>
                <span className="text-xs text-black">{sentiment}</span>
              </div>
            </div>
            
            <div className="flex items-start gap-1.5">
              <span className="text-xs text-black">•</span>
              <div className="flex gap-1.5 flex-wrap">
                <span className="font-bold text-xs text-black">Location:</span>
                <span className="text-xs text-black">{location}</span>
              </div>
            </div>

            <div className="mt-2 space-y-1 ml-3">
              <div className="flex items-start gap-1.5">
                <span className="text-xs text-black">•</span>
                <span className="text-xs text-black">Email (professional tone)</span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="text-xs text-black">•</span>
                <span className="text-xs text-black">Formal memo (official documentation)</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2.5 mt-8">
            <button
              onClick={handleExport}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors shadow-lg text-xs"
            >
              Save →
            </button>

            <button
              onClick={handleEmail}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-colors text-xs"
            >
              Email Summary
            </button>

            <button
              onClick={onClose}
              className="w-full bg-black hover:bg-gray-800 text-white py-3 rounded-xl font-semibold transition-colors text-xs"
            >
              Back to Summary
            </button>
          </div>
        </div>
      </div>

      {/* Editable Report Screen */}
      <EditableReportScreen
        isOpen={showEditableReport}
        onClose={() => setShowEditableReport(false)}
        observation={observation}
        recommendation={recommendation}
        topic={topic}
        sentiment={sentiment}
        location={location}
        date={date}
      />
    </>
  );
}